import { commaTextToStartupArray, startupArrayToCommaText } from '@/components-biz/ImageSelect';
import {
  buildStartupArgFormRowsFromDeployConfigArgs,
  readStartupCommandForRoleFromStartupRoot,
} from '@/delivery-type-plugin/capabilities/maas-system-config/startupCommandsCore';

import {
  type MaasInitCommandApi,
  type MaasInitCommandFormFields,
  argsToScriptText,
  commandsToSpaceText,
  formatInitCommandDisplay,
  initCommandApiToFormFields,
  initCommandFormFieldsToApi,
  isShellPrefillText,
} from '../command-args-normal/utils';
import type { MaasGroupSpec, MaasInitCommand } from '../maas-init/maasTypes';
import {
  type StartupArgDefinition,
  type StartupArgFormRow,
  apiTokensToStartupArgFormRows,
  normalizeStartupArgDefinitions,
  startupArgFormRowsToApiTokens,
} from '../startup-command-args/utils';
import { parseNodesField } from './nodeFields';

export interface InitCommandStartupFormFields {
  command?: string;
  startupArgs?: StartupArgFormRow[];
}

export interface InitCommandStartupFormOptions {
  /** 镜像 hidden 字段 `_imageContainerDeployStartup` 解析结果 */
  startupRoot?: Record<string, unknown> | null;
  role?: 'master' | 'worker';
}

function mergeStartupArgsWithRoleDefinitions(rows: StartupArgFormRow[], definitions: StartupArgDefinition[]): StartupArgFormRow[] {
  if (!definitions.length) return rows;
  const defByName = new Map(definitions.map((item) => [item.name, item]));
  return rows.map((row) => {
    const name = String(row.name ?? '').trim();
    const def = name ? defByName.get(name) : undefined;
    if (!def) return row;
    return { ...row, _self: JSON.stringify(def) };
  });
}

function readRoleStartupArgDefinitions(options?: InitCommandStartupFormOptions): StartupArgDefinition[] {
  if (!options?.startupRoot) return [];
  const role = options.role ?? 'worker';
  const startupItem = readStartupCommandForRoleFromStartupRoot(options.startupRoot, role);
  return normalizeStartupArgDefinitions(startupItem?.args);
}

/** initCommand 是否为 bash/sh 脚本包装（ModelInstance 历史格式） */
export function isScriptWrappedInitCommand(initCommand?: MaasInitCommand | MaasInitCommandApi | null): boolean {
  if (!initCommand?.commands?.length) return false;
  return isShellPrefillText(commandsToSpaceText(initCommand.commands));
}

/** API initCommand → startup 表单（仅 ModelInstance 节点 script 模式 / 展示层使用） */
export function initCommandApiToStartupForm(
  initCommand?: MaasInitCommand | MaasInitCommandApi | null,
  options?: InitCommandStartupFormOptions,
): InitCommandStartupFormFields {
  if (!initCommand?.commands?.length && !initCommand?.args?.length) {
    return {};
  }

  const definitions = readRoleStartupArgDefinitions(options);

  if (isScriptWrappedInitCommand(initCommand)) {
    const legacy = initCommandApiToFormFields(initCommand);
    const script = legacy._argsText?.trim();
    return {
      command: script || undefined,
      startupArgs: [],
    };
  }

  const command = startupArrayToCommaText(initCommand.commands ?? []);
  let startupArgs = apiTokensToStartupArgFormRows(initCommand.args ?? []);
  if (definitions.length) {
    startupArgs = mergeStartupArgsWithRoleDefinitions(startupArgs, definitions);
  }
  return {
    command: command || undefined,
    startupArgs: startupArgs.length ? startupArgs : undefined,
  };
}

/** 表单多行文本 → 启动命令数组（每行一条；无换行时兼容逗号分隔） */
export function formTextToStartupCommandArray(text: unknown): string[] {
  if (Array.isArray(text)) return text.map((s) => String(s).trim()).filter(Boolean);
  if (typeof text !== 'string') return [];
  const trimmed = text.trim();
  if (!trimmed) return [];
  if (trimmed.includes('\n')) {
    return trimmed
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (trimmed.includes(',')) {
    return commaTextToStartupArray(trimmed);
  }
  return [trimmed];
}

/** 启动命令数组 → 表单多行文本（与 containerDeployConfig 多行 command 一致） */
export function startupCommandArrayToFormText(arr: unknown): string {
  if (!Array.isArray(arr)) return typeof arr === 'string' ? arr : '';
  return arr.map((s) => String(s)).join('\n');
}

function readSystemCommandFormText(command: unknown): string {
  if (Array.isArray(command)) return startupCommandArrayToFormText(command);
  if (typeof command === 'string') {
    const trimmed = command.trim();
    if (!trimmed) return '';
    if (trimmed.includes('\n')) return trimmed;
    if (trimmed.includes(',')) return startupCommandArrayToFormText(commaTextToStartupArray(trimmed));
    return trimmed;
  }
  return '';
}

/** startup 表单 → 中间结构（再写入 system.command/args） */
function startupFormToCommandArgs(command: unknown, startupArgs: unknown): MaasInitCommandApi | undefined {
  const commands = formTextToStartupCommandArray(command);
  const rows = Array.isArray(startupArgs) ? (startupArgs as StartupArgFormRow[]) : [];
  const args = startupArgFormRowsToApiTokens(rows);
  if (!commands.length && !args.length) return undefined;
  return {
    commands,
    ...(args.length ? { args } : {}),
  };
}

/** 从 deploy config 预填项构建 startup 表单字段 */
export function buildStartupFormFromDeployCommandItem(
  startupItem: { commandText?: string; args?: unknown[] } | undefined,
  options?: InitCommandStartupFormOptions,
): InitCommandStartupFormFields {
  if (!startupItem?.commandText && !startupItem?.args?.length) {
    return {};
  }
  const command = startupItem.commandText?.trim() || undefined;
  let startupArgs: StartupArgFormRow[] = [];
  if (startupItem.args?.length) {
    startupArgs = buildStartupArgFormRowsFromDeployConfigArgs(startupItem.args);
  }
  const definitions = readRoleStartupArgDefinitions(options);
  if (definitions.length && startupArgs.length) {
    startupArgs = mergeStartupArgsWithRoleDefinitions(startupArgs, definitions);
  }
  return {
    command,
    startupArgs: startupArgs.length ? startupArgs : undefined,
  };
}

/** 判断 startup 表单是否有有效内容 */
export function hasStartupInitCommandFormContent(fields?: InitCommandStartupFormFields | null): boolean {
  if (!fields) return false;
  const hasCommand = Boolean(String(fields.command ?? '').trim());
  const hasArgs = Array.isArray(fields.startupArgs) && fields.startupArgs.some((row) => String(row?.name ?? row?.value ?? '').trim());
  return hasCommand || hasArgs;
}

/** 与节点只读卡片一致：shell 包装命令单行展示；其余命令数组按行展示 */
export function formatNodeStyleCommandMultilineDisplay(initCommand?: MaasInitCommand | MaasInitCommandApi | null): string {
  if (!initCommand) return '-';
  if (isScriptWrappedInitCommand(initCommand)) {
    const commandsText = commandsToSpaceText(initCommand.commands) ?? '';
    const script = argsToScriptText(initCommand.args);
    if (!script) return commandsText || '-';
    return commandsText ? `${commandsText} ${script}`.trim() : script;
  }
  const commandPart = startupCommandArrayToFormText(initCommand.commands ?? []);
  const argsPart = argsToScriptText(initCommand.args);
  const parts = [commandPart, argsPart].filter(Boolean);
  return parts.length ? parts.join('\n') : '-';
}

/** startup 格式 initCommand 展示（命令 + 参数 token） */
export function formatStartupInitCommandDisplay(initCommand?: MaasInitCommand | MaasInitCommandApi | null): string {
  if (!initCommand) return '-';
  if (isScriptWrappedInitCommand(initCommand)) {
    const legacy = initCommandApiToFormFields(initCommand);
    const script = legacy._argsText?.trim();
    if (!script) return commandsToSpaceText(initCommand.commands) || '-';
    return `${commandsToSpaceText(initCommand.commands)} ${script}`.trim();
  }
  const commandPart = startupArrayToCommaText(initCommand.commands ?? []);
  const argsPart = (initCommand.args ?? []).join(' ');
  const parts = [commandPart, argsPart].filter(Boolean);
  return parts.length ? parts.join(' ') : '-';
}

/** system.command/args → CommandArgsNormalBlock 表单字段（MI/TC 节点启动命令） */
export function systemCommandArgsToFormFields(system?: Record<string, unknown> | null): MaasInitCommandFormFields {
  return initCommandApiToFormFields(normalizeSystemCommandArgsApi(system));
}

/** CommandArgsNormalBlock 表单字段 → system.command/args */
export function formFieldsToSystemCommandArgs(fields: MaasInitCommandFormFields | undefined): { command?: string[]; args?: string[] } {
  const parsed = initCommandFormFieldsToApi(fields);
  if (!parsed) return {};
  return {
    ...(parsed.commands.length ? { command: parsed.commands } : {}),
    ...(parsed.args?.length ? { args: parsed.args } : {}),
  };
}

/** initCommand API 结构 → system.command/args */
export function initCommandApiToSystemCommandArgs(initCommand?: MaasInitCommandApi | null): { command?: string[]; args?: string[] } {
  if (!initCommand?.commands?.length && !initCommand?.args?.length) return {};
  return {
    ...(initCommand.commands?.length ? { command: initCommand.commands } : {}),
    ...(initCommand.args?.length ? { args: initCommand.args } : {}),
  };
}

function normalizeSystemCommandArgsApi(system?: Record<string, unknown> | null): MaasInitCommandApi | undefined {
  if (!system) return undefined;
  const command = system.command;
  const args = system.args;
  const commands = Array.isArray(command)
    ? command.map((item) => String(item ?? '').trim()).filter(Boolean)
    : typeof command === 'string' && command.trim()
      ? [command.trim()]
      : [];
  const normalizedArgs = Array.isArray(args)
    ? args.map((item) => String(item ?? '').trim()).filter(Boolean)
    : typeof args === 'string' && args.trim()
      ? [args.trim()]
      : [];
  if (!commands.length && !normalizedArgs.length) return undefined;
  return {
    commands,
    ...(normalizedArgs.length ? { args: normalizedArgs } : {}),
  };
}

/** 节点 system.command/args 展示（与 initCommand 同款 bash/sh 脚本格式） */
export function formatGroupSystemCommandArgsDisplay(system?: Record<string, unknown> | null): string {
  const api = normalizeSystemCommandArgsApi(system);
  if (!api) return '-';
  return formatInitCommandDisplay(api);
}

/** 判断 system.command/args 是否有内容（CommandArgsNormalBlock 映射） */
export function hasSystemCommandArgsContent(system?: Record<string, unknown> | null): boolean {
  return !!normalizeSystemCommandArgsApi(system);
}

/** 判断 group.system 是否含启动命令/参数 */
export function hasSystemStartupContent(system?: Record<string, unknown> | null): boolean {
  if (!system) return false;
  const command = system.command;
  const args = system.args;
  const hasCommand = Array.isArray(command) ? command.length > 0 : Boolean(String(command ?? '').trim());
  const hasArgs = Array.isArray(args) ? args.length > 0 : Boolean(String(args ?? '').trim());
  return hasCommand || hasArgs;
}

/** group.system.command/args → startup 表单 */
export function systemStartupToStartupForm(
  system?: Record<string, unknown> | null,
  options?: InitCommandStartupFormOptions,
): InitCommandStartupFormFields {
  if (!hasSystemStartupContent(system)) return {};
  const command = readSystemCommandFormText(system?.command);
  const rawArgs = system?.args;
  let startupArgs: StartupArgFormRow[] = [];
  if (Array.isArray(rawArgs)) {
    if (rawArgs.length && typeof rawArgs[0] === 'object') {
      startupArgs = (rawArgs as StartupArgFormRow[]).map((row) => ({
        name: String(row.name ?? ''),
        value: String(row.value ?? ''),
        _self: row._self,
      }));
    } else {
      startupArgs = apiTokensToStartupArgFormRows(rawArgs as string[]);
    }
  } else if (typeof rawArgs === 'string' && rawArgs.trim()) {
    startupArgs = apiTokensToStartupArgFormRows(commaTextToStartupArray(rawArgs));
  }
  const definitions = readRoleStartupArgDefinitions(options);
  if (definitions.length && startupArgs.length) {
    startupArgs = mergeStartupArgsWithRoleDefinitions(startupArgs, definitions);
  }
  return {
    command: command || undefined,
    startupArgs: startupArgs.length ? startupArgs : undefined,
  };
}

/** startup 表单 → 节点 system.command/args（command 直接存数组，避免 normalize 按空格误拆） */
export function startupFormToGroupSystemStartup(command: unknown, startupArgs: unknown): { command?: string[]; args?: string[] } {
  const parsed = startupFormToCommandArgs(command, startupArgs);
  if (!parsed) return {};
  return {
    ...(parsed.commands.length ? { command: parsed.commands } : {}),
    ...(parsed.args?.length ? { args: parsed.args } : {}),
  };
}

/** 节点组启动命令回显：仅读 system.command/args */
export function readGroupSystemStartupFormFields(
  group?: { system?: Record<string, unknown> },
  options?: InitCommandStartupFormOptions,
): InitCommandStartupFormFields {
  return systemStartupToStartupForm(group?.system, options);
}

/** 节点 system 启动命令展示 */
export function formatGroupSystemStartupDisplay(system?: Record<string, unknown> | null): string {
  if (!hasSystemStartupContent(system)) return '-';
  const commandPart = Array.isArray(system?.command) ? startupArrayToCommaText(system.command) : String(system?.command ?? '').trim();
  const argsPart = Array.isArray(system?.args) ? (system.args as string[]).join(' ') : String(system?.args ?? '').trim();
  const parts = [commandPart, argsPart].filter(Boolean);
  return parts.length ? parts.join(' ') : '-';
}

/** 提交前移除节点组 initCommand（应用实例仅使用 system.command/args） */
export function stripGroupInitCommand<T extends MaasGroupSpec>(group: T): T {
  const next = { ...group };
  delete next.initCommand;
  return next;
}

/** 节点 JSON 字段回填时剥离 initCommand */
export function stripInitCommandFromNodesJson(nodesJson: string): string {
  const nodes = parseNodesField(nodesJson);
  if (!nodes.length) return nodesJson;
  return JSON.stringify(nodes.map((node) => stripGroupInitCommand(node)));
}
